import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import {
    PageHeader, Card, LoadingState, ErrorState,
    CopperButton, SlateButton, Field, TextInput, NumberInput, Select, Pill,
} from "@/components/common/UI";
import WhyModal from "@/components/common/WhyModal";
import InfoTip from "@/components/common/InfoTip";
import {
    UploadCloud, FileText, Sparkles, Lightbulb, ArrowRight, CheckCircle2,
    AlertTriangle, Cpu, HelpCircle, Loader2, X, Plus, Pencil, Search, Package,
    Layers, FileSpreadsheet, TrendingUp, TrendingDown, Trash2,
} from "lucide-react";
import { toast } from "sonner";
import { fmtINR } from "@/lib/format";

const FORMULA_VARIANTS = ["STANDARD", "KOBELCO_CE", "VOLVO_EXPORT", "FES_LYNX", "JCB_DOMESTIC"];

export default function AIZBCWizard() {
    const navigate = useNavigate();

    // Input mode: Upload PDF | Manual entry | Load from Catalog
    const [inputMode, setInputMode] = useState("upload"); // upload | manual | catalog

    // Setup form
    const [customers, setCustomers] = useState([]);
    const [plants, setPlants] = useState([]);
    const [customerId, setCustomerId] = useState("");
    const [plantId, setPlantId] = useState("");
    const [files, setFiles] = useState([]);
    const [dragOver, setDragOver] = useState(false);
    const fileInputRef = useRef(null);

    // Inline new-client mini-modal
    const [showNewClient, setShowNewClient] = useState(false);
    const [newClientName, setNewClientName] = useState("");
    const [newClientVariant, setNewClientVariant] = useState("STANDARD");
    const [creatingClient, setCreatingClient] = useState(false);

    // Master mapping table: used to auto-populate machine/rate when user picks an op name
    const [mappings, setMappings] = useState([]);

    // Manual entry form state
    const [manualForm, setManualForm] = useState({
        part_number: "", part_name: "", drawing_number: "", material: "MILD_STEEL",
        shape_family: "PLATE",
        length_mm: "", width_mm: "", thickness_mm: "",
        diameter_mm: "", height_mm: "",
        holes_count: 0, threads_count: 0, chamfers_count: 0,
        has_welding: false, welding_length_mm: 0,
    });
    const [creatingManual, setCreatingManual] = useState(false);

    // Catalog search
    const [catalogQuery, setCatalogQuery] = useState("");
    const [catalogResults, setCatalogResults] = useState([]);
    const [catalogLoading, setCatalogLoading] = useState(false);

    // Job
    const [uploading, setUploading] = useState(false);
    const [job, setJob] = useState(null);
    const [selectedPart, setSelectedPart] = useState(null);
    const [partDetail, setPartDetail] = useState(null);

    // Combined summary (multi-part jobs)
    const [combined, setCombined] = useState(null);

    // Inline edit modes (identity + blank)
    const [editIdentity, setEditIdentity] = useState(false);
    const [editBlank, setEditBlank] = useState(false);
    const [identityDraft, setIdentityDraft] = useState({});
    const [blankDraft, setBlankDraft] = useState({});

    // Per-part user inputs
    const [qty, setQty] = useState(1);
    const [rmRate, setRmRate] = useState("");
    const [scrapRate, setScrapRate] = useState("");
    const [saving, setSaving] = useState(false);
    const [savedZbcs, setSavedZbcs] = useState({}); // partId → zbcId

    // Why modal
    const [whyOpen, setWhyOpen] = useState(false);
    const [whyData, setWhyData] = useState({ title: "", data: {} });

    // Supplier ZBC comparison (per selected part)
    const [supplierComparison, setSupplierComparison] = useState(null);
    const [supplierBusy, setSupplierBusy] = useState(false);
    const supplierFileInputRef = useRef(null);

    // GAP 5 · Unit WK utilisation % from Formula Registry (default 78%).
    const [unitUtilizationPct, setUnitUtilizationPct] = useState(78);

    useEffect(() => {
        Promise.all([
            api.get("/customers"),
            api.get("/plants"),
            api.get("/process-mappings"),
            api.get("/formulas").catch(() => ({ data: {} })),
        ])
            .then(([c, p, m, f]) => {
                setCustomers(c.data);
                setPlants(p.data);
                setMappings(m.data);
                const v = parseFloat(f?.data?.unit_wk_utilization_pct);
                if (!Number.isNaN(v) && v > 0) setUnitUtilizationPct(v);
            })
            .catch((e) => toast.error(formatApiError(e)));
    }, []);

    // Reload combined summary whenever the job or its parts change
    useEffect(() => {
        if (!job?.id) return;
        api.get(`/zbc/ai/jobs/${job.id}/combined`)
            .then((r) => setCombined(r.data))
            .catch(() => {});
    }, [job?.id, savedZbcs]);

    useEffect(() => {
        if (selectedPart) {
            api.get(`/zbc/ai/parts/${selectedPart}`)
                .then((r) => {
                    setPartDetail(r.data);
                    setIdentityDraft({});
                    setBlankDraft({});
                    setEditIdentity(false);
                    setEditBlank(false);
                    // GAP 3A: If Claude extracted qty_per_assembly from the
                    // title block, pre-fill the Quantity field. User can still
                    // override on the Save-as-ZBC panel.
                    if (r.data.ai_qty_suggestion && r.data.ai_qty_suggestion > 0) {
                        setQty(r.data.ai_qty_suggestion);
                    }
                })
                .catch((e) => toast.error(formatApiError(e)));
        }
    }, [selectedPart]);

    // ---------- Handlers ----------
    const onDrop = (e) => {
        e.preventDefault();
        setDragOver(false);
        const dropped = Array.from(e.dataTransfer.files || []).filter((f) =>
            f.name.toLowerCase().endsWith(".pdf")
        );
        setFiles((prev) => {
            const merged = [...prev, ...dropped];
            if (merged.length > 10) {
                toast.error(`Only 10 drawings allowed per upload; keeping the first 10 (dropped ${merged.length - 10}).`);
                return merged.slice(0, 10);
            }
            return merged;
        });
    };

    const onPick = (e) => {
        const picked = Array.from(e.target.files || []);
        setFiles((prev) => {
            const merged = [...prev, ...picked];
            if (merged.length > 10) {
                toast.error(`Only 10 drawings allowed per upload; keeping the first 10 (dropped ${merged.length - 10}).`);
                return merged.slice(0, 10);
            }
            return merged;
        });
    };

    const removeFile = (i) => setFiles(files.filter((_, idx) => idx !== i));

    const uploadAll = async () => {
        if (files.length === 0) return toast.error("Add at least one PDF");
        if (files.length > 10) return toast.error("Maximum 10 drawings per upload. Please split into smaller batches.");
        setUploading(true);
        try {
            const fd = new FormData();
            if (customerId) fd.append("customer_id", customerId);
            if (plantId) fd.append("plant_id", plantId);
            files.forEach((f) => fd.append("files", f));
            const { data } = await api.post("/zbc/ai/upload", fd, {
                headers: { "Content-Type": "multipart/form-data" },
                timeout: 5 * 60 * 1000,
            });
            const jobRes = await api.get(`/zbc/ai/jobs/${data.job_id}`);
            setJob(jobRes.data);
            if (jobRes.data.parts_detail?.length) {
                setSelectedPart(jobRes.data.parts_detail[0].id);
            }
            toast.success(`Intelligent extraction complete for ${data.part_ids.length} drawing(s)`);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setUploading(false);
        }
    };

    // ---- Inline new-client ----
    const createNewClient = async () => {
        if (!newClientName.trim()) return toast.error("Client name is required");
        setCreatingClient(true);
        try {
            const { data } = await api.post("/customers/quick", {
                name: newClientName.trim(),
                formula_variant: newClientVariant,
            });
            setCustomers((prev) => [...prev, data]);
            setCustomerId(data.id);
            setShowNewClient(false);
            setNewClientName("");
            toast.success(`Client "${data.name}" added`);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setCreatingClient(false);
        }
    };

    // ---- Manual entry ----
    const submitManual = async () => {
        const f = manualForm;
        if (!f.part_number.trim() && !f.part_name.trim())
            return toast.error("Enter Part Number or Part Name");
        const geometry = {
            shape_family: f.shape_family,
            length_mm: parseFloat(f.length_mm) || null,
            width_mm: parseFloat(f.width_mm) || null,
            thickness_mm: parseFloat(f.thickness_mm) || null,
            diameter_mm: parseFloat(f.diameter_mm) || null,
            height_mm: parseFloat(f.height_mm) || null,
        };
        const features = {
            holes: parseInt(f.holes_count) > 0 ? [{ dia_mm: 10, qty: parseInt(f.holes_count) }] : [],
            threads: parseInt(f.threads_count) > 0 ? [{ spec: "M8", qty: parseInt(f.threads_count) }] : [],
            chamfers: parseInt(f.chamfers_count) > 0 ? [{ size_mm: 1, angle_deg: 45, qty: parseInt(f.chamfers_count) }] : [],
            welding_symbols: f.has_welding ? ["MIG"] : [],
        };
        setCreatingManual(true);
        try {
            const body = {
                job_id: job?.id || null,
                customer_id: customerId || null,
                plant_id: plantId || null,
                identity: {
                    part_number: f.part_number.trim(),
                    part_name: f.part_name.trim(),
                    drawing_number: f.drawing_number.trim(),
                    material: f.material,
                },
                geometry, features,
            };
            const { data } = await api.post("/zbc/ai/parts/manual", body);
            const jobRes = await api.get(`/zbc/ai/jobs/${data.job_id}`);
            setJob(jobRes.data);
            setSelectedPart(data.part_id);
            toast.success(`Part created from manual entry, ${data.part.ops?.length || 0} ops derived`);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setCreatingManual(false);
        }
    };

    // ---- Catalog search ----
    const searchCatalog = async () => {
        setCatalogLoading(true);
        try {
            const { data } = await api.get(
                `/zbc/ai/catalog?q=${encodeURIComponent(catalogQuery)}&limit=25`
            );
            setCatalogResults(data.items || []);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setCatalogLoading(false);
        }
    };

    const cloneFromCatalog = async (zbcId) => {
        try {
            const { data } = await api.post("/zbc/ai/parts/from-catalog", {
                zbc_id: zbcId,
                job_id: job?.id || null,
                customer_id: customerId || null,
                plant_id: plantId || null,
            });
            const jobRes = await api.get(`/zbc/ai/jobs/${data.job_id}`);
            setJob(jobRes.data);
            setSelectedPart(data.part_id);
            toast.success("Loaded from catalog, you can now edit everything");
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    // ---- Save identity/blank edits ----
    const saveIdentity = async () => {
        try {
            const { data } = await api.put(`/zbc/ai/parts/${selectedPart}/edit`, {
                identity: identityDraft, reason: "Manual identity correction",
            });
            setPartDetail(data.part);
            setEditIdentity(false);
            setIdentityDraft({});
            toast.success(`Updated ${data.changed} identity field(s)`);
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };
    const saveBlank = async () => {
        try {
            // GAP 1: finish_weight_kg lives inside raw.geometry (not blank). Split it out
            // so the /edit endpoint routes it to `geometry.finish_weight_kg`.
            const { finish_weight_kg, ...blankOnly } = blankDraft;
            const cleaned = Object.fromEntries(
                Object.entries(blankOnly).map(([k, v]) => [
                    k,
                    typeof v === "string" && !isNaN(parseFloat(v)) ? parseFloat(v) : v,
                ])
            );
            const body = { blank: cleaned, reason: "Manual blank correction" };
            if (finish_weight_kg !== undefined && finish_weight_kg !== "") {
                body.geometry = { finish_weight_kg: parseFloat(finish_weight_kg) };
            }
            const { data } = await api.put(`/zbc/ai/parts/${selectedPart}/edit`, body);
            setPartDetail(data.part);
            setEditBlank(false);
            setBlankDraft({});
            toast.success(`Updated ${data.changed} field(s)`);
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    // ---- Op edits (Add / Edit / Delete conversion rows) ----
    const [opsDraft, setOpsDraft] = useState(null);
    const [opsSaving, setOpsSaving] = useState(false);
    const [opsDirty, setOpsDirty] = useState(false);

    useEffect(() => {
        if (partDetail) {
            // User asked (Session 13): don't auto-list AI-inferred ops on the
            // conversion cost table. Start empty; user adds rows manually
            // (or clicks "Load Intelligent Suggestions" to restore the inference).
            setOpsDraft([]);
            setOpsDirty(false);
        }
    }, [partDetail?.id]);   // reset when a different part is selected

    const loadAiSuggestions = () => {
        if (!partDetail?.ops?.length) {
            toast.info("No intelligently-inferred operations for this part");
            return;
        }
        setOpsDraft(partDetail.ops.map((op, i) => ({ ...op, _rowId: i })));
        // Populated from a canonical AI source; no unsaved user edits, so
        // keep the Save button hidden until the user actually tweaks something.
        setOpsDirty(false);
        // Nudge the ops table into view so the user sees the change on
        // long review pages.
        setTimeout(() => {
            document.querySelector('[data-testid="add-op-row"]')
                ?.scrollIntoView({ behavior: "smooth", block: "center" });
        }, 60);
    };

    const updateOpField = (rowIdx, field, value) => {
        setOpsDraft((prev) => {
            const next = [...prev];
            const op = { ...next[rowIdx], [field]: value };
            // If user picked an op-name that exists in the mapping master,
            // auto-fill machine + rate from the ABC COMPANY standard.
            if (field === "op" && value) {
                const hit = (mappings || []).find(
                    (m) => (m.operation_name || "").toLowerCase() === String(value).toLowerCase()
                );
                if (hit) {
                    op.machine_code = hit.mapped_machine || hit.mapped_process || op.machine_code || "";
                    // Auto-populate rate only if user hasn't already set one
                    if (!op.machine_hour_rate || Number(op.machine_hour_rate) === 0) {
                        op.machine_hour_rate = hit.standard_rate;
                    }
                    op.uom = hit.uom || op.uom;
                    op._mapped_from = hit.id;
                }
            }
            // live-recompute cost
            const rate = parseFloat(op.machine_hour_rate) || 0;
            const secs = parseFloat(op.cycle_time_seconds) || 0;
            op.conversion_cost = Math.round(rate * secs / 3600 * 10000) / 10000;
            op.productivity_pph = secs ? Math.round(3600 / secs * 100) / 100 : 0;
            next[rowIdx] = op;
            return next;
        });
        setOpsDirty(true);
    };

    const deleteOpRow = (rowIdx) => {
        setOpsDraft((prev) => prev.filter((_, i) => i !== rowIdx));
        setOpsDirty(true);
    };

    const addOpRow = () => {
        setOpsDraft((prev) => [
            ...prev,
            {
                _rowId: Date.now(),
                op: "",
                machine_code: "",
                machine_family: "MANUAL",
                machine_hour_rate: "",
                cycle_time_seconds: "",
                conversion_cost: 0,
                productivity_pph: 0,
                reason: "User-added operation (blank, pick from master or type manually)",
            },
        ]);
        setOpsDirty(true);
    };

    const saveOps = async () => {
        setOpsSaving(true);
        try {
            const payload = {
                ops: opsDraft.map(({ _rowId, ...rest }) => rest),
                reason: "Manual conversion-cost edit",
            };
            await api.put(`/zbc/ai/parts/${selectedPart}/ops`, payload);
            const { data } = await api.get(`/zbc/ai/parts/${selectedPart}`);
            setPartDetail(data);
            toast.success("Conversion cost updated");
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setOpsSaving(false);
        }
    };

    // ---- Delete an AI part entry ----
    const deletePart = async (p) => {
        if (!window.confirm(`Remove Intelligent part "${p.part_number || p.filename}"? This does not delete any saved ZBC.`))
            return;
        try {
            await api.delete(`/zbc/ai/parts/${p.id}`);
            toast.success("Part removed");
            // Reload the current job
            if (job?.id) {
                const jobRes = await api.get(`/zbc/ai/jobs/${job.id}`);
                setJob(jobRes.data);
                if (selectedPart === p.id) {
                    const remaining = jobRes.data.parts_detail || [];
                    setSelectedPart(remaining[0]?.id || null);
                    if (remaining.length === 0) {
                        setPartDetail(null);
                    }
                }
            }
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const setContextIfNeeded = async () => {
        if (!partDetail?.customer_id || !partDetail?.plant_id) {
            if (!customerId || !plantId) {
                toast.error("Pick Customer & Plant above before saving");
                return false;
            }
            try {
                await api.put(`/zbc/ai/parts/${selectedPart}/context`, {
                    customer_id: customerId,
                    plant_id: plantId,
                });
                return true;
            } catch (e) {
                toast.error(formatApiError(e));
                return false;
            }
        }
        return true;
    };

    // ---- Supplier ZBC upload + manual entry + comparison ----
    const fetchSupplierComparison = async (partId = selectedPart) => {
        if (!partId) return;
        try {
            const params = new URLSearchParams({
                quantity: String(qty || 1),
                rm_rate: String(rmRate || 0),
                scrap_rate: String(scrapRate || 0),
            });
            const { data } = await api.get(`/zbc/ai/parts/${partId}/supplier-comparison?${params}`);
            setSupplierComparison(data);
        } catch (e) {
            // Silent: comparison is optional context
            setSupplierComparison(null);
        }
    };

    const uploadSupplierZbc = async (file) => {
        if (!selectedPart) return;
        if (!file.name.toLowerCase().match(/\.(xlsx|xlsm)$/)) {
            return toast.error("Please pick an .xlsx / .xlsm supplier ZBC sheet");
        }
        setSupplierBusy(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post(
                `/zbc/ai/parts/${selectedPart}/supplier-zbc/upload`,
                fd,
                { headers: { "Content-Type": "multipart/form-data" }, timeout: 90000 }
            );
            toast.success(`Parsed supplier ZBC from "${data.supplier_zbc.sheet_name || "sheet"}" · ${data.sheets_found.length} sheet(s) found`);
            await fetchSupplierComparison();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setSupplierBusy(false);
            if (supplierFileInputRef.current) supplierFileInputRef.current.value = "";
        }
    };

    const saveSupplierManual = async (supplierName, values) => {
        if (!selectedPart) return;
        setSupplierBusy(true);
        try {
            await api.post(`/zbc/ai/parts/${selectedPart}/supplier-zbc/manual`, {
                supplier_name: supplierName,
                values,
            });
            toast.success("Supplier ZBC saved");
            await fetchSupplierComparison();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setSupplierBusy(false);
        }
    };

    const clearSupplierZbc = async () => {
        if (!selectedPart) return;
        if (!window.confirm("Remove the uploaded supplier ZBC for this part?")) return;
        try {
            await api.delete(`/zbc/ai/parts/${selectedPart}/supplier-zbc`);
            setSupplierComparison(null);
            toast.info("Supplier ZBC removed");
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    // Auto-refresh comparison when part changes or the user tweaks Qty/RM/Scrap
    useEffect(() => {
        if (selectedPart) fetchSupplierComparison(selectedPart);
    }, [selectedPart]);
    useEffect(() => {
        // Debounced refresh: whenever inputs change and supplier data is present
        if (!selectedPart || !supplierComparison?.has_supplier_data) return;
        const t = setTimeout(() => fetchSupplierComparison(selectedPart), 400);
        return () => clearTimeout(t);
    }, [qty, rmRate, scrapRate, opsDraft]);

    const saveAsZBC = async () => {
        const ctx = await setContextIfNeeded();
        if (!ctx) return;
        if (!rmRate || parseFloat(rmRate) <= 0) return toast.error("Enter RM rate");
        setSaving(true);
        try {
            // Always ship the user's current opsDraft to the backend so the
            // saved ZBC uses ONLY what the user has entered on the conversion
            // cost table (empty = zero conversion). Falls back to part.ops on
            // the backend if the field is missing.
            const cleanOps = (opsDraft || []).map(({ _rowId, _mapped_from, ...rest }) => ({
                op: rest.op || "",
                machine_code: rest.machine_code || "",
                machine_family: rest.machine_family || "MANUAL",
                machine_hour_rate: parseFloat(rest.machine_hour_rate) || 0,
                cycle_time_seconds: parseFloat(rest.cycle_time_seconds) || 0,
                conversion_cost: parseFloat(rest.conversion_cost) || 0,
                productivity_pph: parseFloat(rest.productivity_pph) || 0,
                uom: rest.uom || "PER_PIECE",
            }));
            const { data } = await api.post(`/zbc/ai/parts/${selectedPart}/save-as-zbc`, {
                quantity: parseFloat(qty),
                rm_rate: parseFloat(rmRate),
                scrap_rate: parseFloat(scrapRate || 0),
                customer_id: customerId || undefined,
                plant_id: plantId || undefined,
                ops: cleanOps,
            });
            setSavedZbcs({ ...savedZbcs, [selectedPart]: data.zbc_id });
            const versionSuffix = data.version && data.version > 1 ? ` (v${data.version})` : "";
            toast.success(`ZBC created${versionSuffix} · Should Cost ₹${data.should_cost}`);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setSaving(false);
        }
    };

    const openWhy = (title, data, drawingRef) => {
        setWhyData({ title, data, drawingRef });
        setWhyOpen(true);
    };

    // ---------- Steps ----------
    const step = !job ? 1 : selectedPart && partDetail ? 2 : 1.5;

    // ---------- Render ----------
    return (
        <div data-testid="ai-zbc-wizard">
            <PageHeader
                eyebrow="ZBC · INTELLIGENTLY GENERATED"
                title="Intelligent Drawing Analysis"
                subtitle="Upload up to 10 engineering drawings (PDF) per batch. CostIQ extracts geometry & features with Claude Sonnet 4.5 (fallback GPT-5.2), derives raw material and manufacturing route, and pre-fills the ZBC. Saving a duplicate part number creates a NEW version; nothing is overwritten."
                actions={
                    <SlateButton variant="outline" onClick={() => navigate("/zbc")}>
                        ← Back to ZBC List
                    </SlateButton>
                }
            />

            {/* Step indicator */}
            <div className="flex items-center gap-3 mb-5">
                <StepDot n={1} label="Upload" active={step === 1} done={step > 1} />
                <StepArrow />
                <StepDot n={2} label="Review Intelligent Extract" active={step === 2} done={false} />
                <StepArrow />
                <StepDot n={3} label="Save ZBC" active={false} done={false} />
            </div>

            {/* ============== STEP 1: SETUP + INPUT MODE ============== */}
            {step === 1 && (
                <Card>
                    <div className="p-5 space-y-5">
                        {/* Customer + Plant with inline "+ New Client" */}
                        <div className="grid grid-cols-2 gap-4">
                            <Field label="Customer">
                                <div className="flex items-stretch gap-1.5">
                                    <Select
                                        value={customerId}
                                        onChange={(e) => setCustomerId(e.target.value)}
                                        data-testid="ai-customer"
                                        className="flex-1"
                                    >
                                        <option value="">— (optional, pick later before saving) —</option>
                                        {customers.map((c) => (
                                            <option key={c.id} value={c.id}>{c.code} · {c.name}</option>
                                        ))}
                                    </Select>
                                    <button
                                        type="button"
                                        onClick={() => setShowNewClient((v) => !v)}
                                        className="px-2.5 rounded-md border border-slate-200 hover:border-[hsl(var(--copper))] hover:bg-amber-50/60 text-[hsl(var(--copper))] transition-colors"
                                        title="Add new client"
                                        data-testid="ai-new-client-btn"
                                    >
                                        <Plus className="w-4 h-4" />
                                    </button>
                                </div>
                                {showNewClient && (
                                    <div className="mt-2 p-3 border border-amber-200 bg-amber-50/40 rounded-md space-y-2" data-testid="new-client-form">
                                        <div className="text-[11px] uppercase tracking-widest text-slate-600 font-semibold">Add new client</div>
                                        <TextInput
                                            placeholder="Client name (e.g. Ashok Leyland Ltd.)"
                                            value={newClientName}
                                            onChange={(e) => setNewClientName(e.target.value)}
                                            data-testid="new-client-name"
                                        />
                                        <Select
                                            value={newClientVariant}
                                            onChange={(e) => setNewClientVariant(e.target.value)}
                                            data-testid="new-client-variant"
                                        >
                                            {FORMULA_VARIANTS.map((v) => <option key={v} value={v}>{v}</option>)}
                                        </Select>
                                        <div className="flex justify-end gap-2">
                                            <SlateButton
                                                variant="outline"
                                                onClick={() => { setShowNewClient(false); setNewClientName(""); }}
                                                className="h-7 text-[11px]"
                                            >Cancel</SlateButton>
                                            <CopperButton
                                                onClick={createNewClient}
                                                disabled={creatingClient || !newClientName.trim()}
                                                className="h-7 text-[11px]"
                                                data-testid="new-client-save"
                                            >
                                                {creatingClient ? "Adding…" : "Add & Select"}
                                            </CopperButton>
                                        </div>
                                    </div>
                                )}
                            </Field>
                            <Field label="Plant">
                                <Select value={plantId} onChange={(e) => setPlantId(e.target.value)} data-testid="ai-plant">
                                    <option value="">— (optional, pick later before saving) —</option>
                                    {plants.filter((p) => p.is_active !== false).map((p) => (
                                        <option key={p.id} value={p.id}>{p.code} · {p.name}</option>
                                    ))}
                                </Select>
                            </Field>
                        </div>

                        {/* Input-mode tabs */}
                        <div className="flex items-center gap-1 border-b border-slate-200 -mb-1">
                            <ModeTab active={inputMode === "upload"} onClick={() => setInputMode("upload")} icon={UploadCloud} label="Upload Drawing" testid="tab-upload" />
                            <ModeTab active={inputMode === "manual"} onClick={() => setInputMode("manual")} icon={Pencil} label="Manual Entry" testid="tab-manual" />
                            <ModeTab active={inputMode === "catalog"} onClick={() => setInputMode("catalog")} icon={Package} label="Load from Catalog" testid="tab-catalog" />
                        </div>

                        {/* ---- UPLOAD MODE ---- */}
                        {inputMode === "upload" && (
                            <div className="space-y-4">
                                <div
                                    onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                                    onDragLeave={() => setDragOver(false)}
                                    onDrop={onDrop}
                                    onClick={() => fileInputRef.current?.click()}
                                    className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
                                        dragOver
                                            ? "border-[hsl(var(--copper))] bg-amber-50/60"
                                            : "border-slate-300 hover:border-[hsl(var(--copper))] hover:bg-slate-50"
                                    }`}
                                    data-testid="ai-dropzone"
                                >
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        accept=".pdf"
                                        multiple
                                        hidden
                                        onChange={onPick}
                                    />
                                    <UploadCloud className="w-10 h-10 mx-auto text-[hsl(var(--copper))] mb-2" />
                                    <div className="text-[15px] font-semibold text-slate-800">
                                        Drop PDF drawings here or click to browse
                                    </div>
                                    <div className="text-[12px] text-slate-500 mt-1">
                                        One PDF = one part. Multiple drawings supported; a combined summary is generated.
                                    </div>
                                </div>

                                {files.length > 0 && (
                                    <div className="space-y-1.5">
                                        <div className="eyebrow text-slate-500">{files.length} file(s) queued</div>
                                        {files.map((f, i) => (
                                            <div key={i} className="flex items-center justify-between px-3 py-2 rounded border border-slate-200 bg-white text-[13px]">
                                                <div className="flex items-center gap-2">
                                                    <FileText className="w-4 h-4 text-slate-500" />
                                                    <span>{f.name}</span>
                                                    <span className="text-slate-400 text-[11px]">
                                                        {(f.size / 1024).toFixed(1)} KB
                                                    </span>
                                                </div>
                                                <button onClick={() => removeFile(i)} className="p-1 hover:bg-slate-100 rounded" title="Remove">
                                                    <X className="w-3.5 h-3.5 text-slate-400" />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                <div className="flex justify-end">
                                    <CopperButton onClick={uploadAll} disabled={uploading || files.length === 0} data-testid="ai-upload-btn">
                                        {uploading ? (
                                            <><Loader2 className="w-4 h-4 animate-spin" /> Analysing drawings…</>
                                        ) : (
                                            <><Sparkles className="w-4 h-4" /> Analyse Intelligently</>
                                        )}
                                    </CopperButton>
                                </div>
                            </div>
                        )}

                        {/* ---- MANUAL MODE ---- */}
                        {inputMode === "manual" && (
                            <ManualEntryForm
                                form={manualForm}
                                setForm={setManualForm}
                                onSubmit={submitManual}
                                submitting={creatingManual}
                            />
                        )}

                        {/* ---- CATALOG MODE ---- */}
                        {inputMode === "catalog" && (
                            <CatalogPanel
                                query={catalogQuery}
                                setQuery={setCatalogQuery}
                                results={catalogResults}
                                loading={catalogLoading}
                                onSearch={searchCatalog}
                                onPick={cloneFromCatalog}
                            />
                        )}

                        <div className="text-[11px] text-slate-500 flex items-start gap-2 bg-amber-50/60 border border-amber-100 rounded p-2.5">
                            <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5 text-amber-600" />
                            All values are fully editable after extraction. Conversion cost operations can be picked from the Process Mapping master (ABC COMPANY standard rates) or entered manually.
                        </div>
                    </div>
                </Card>
            )}

            {/* Combined summary bar (shows only when a multi-part job is loaded) */}
            {job && combined && combined.parts_count > 1 && (
                <Card className="mb-4">
                    <div className="p-4 flex items-center gap-6">
                        <div className="flex items-center gap-2 text-slate-800 font-semibold">
                            <Layers className="w-4 h-4 text-[hsl(var(--copper))]" />
                            <span className="text-[13px]">Combined Summary · {combined.parts_count} part(s)</span>
                        </div>
                        <div className="flex-1 grid grid-cols-4 gap-6">
                            <SummaryStat label="Total Should-Cost (assembly)" value={fmtINR(combined.assembly.total_should_cost, 2)} />
                            <SummaryStat label="Total Conversion" value={fmtINR(combined.assembly.total_conversion_cost, 2)} />
                            <SummaryStat label="Saved ZBCs" value={`${combined.saved_zbc_count} / ${combined.parts_count}`} />
                            <SummaryStat label="Total Quantity" value={String(combined.assembly.total_quantity)} />
                        </div>
                    </div>
                </Card>
            )}


            {/* ============== STEP 2: REVIEW ============== */}
            {job && (
                <div className="grid grid-cols-[280px_1fr] gap-4">
                    <Card>
                        <div className="p-3">
                            <div className="eyebrow text-slate-500 mb-2 px-1">Parts detected</div>
                            <div className="space-y-1">
                                {job.parts_detail?.map((p) => (
                                    <div
                                        key={p.id}
                                        className={`group w-full rounded text-[12.5px] transition-colors ${
                                            selectedPart === p.id
                                                ? "bg-[hsl(var(--copper))]/10 border border-[hsl(var(--copper))]/30"
                                                : "hover:bg-slate-50 border border-transparent"
                                        }`}
                                        data-testid={`ai-part-${p.id}`}
                                    >
                                        <button
                                            onClick={() => setSelectedPart(p.id)}
                                            className="w-full text-left px-3 py-2"
                                        >
                                            <div className="flex items-start justify-between gap-2">
                                                <div className="min-w-0">
                                                    <div className="font-mono font-semibold truncate">{p.part_number || "(unrecognized)"}</div>
                                                    <div className="text-[11px] text-slate-500 truncate">{p.filename}</div>
                                                </div>
                                                {savedZbcs[p.id] && <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />}
                                            </div>
                                            <div className="flex items-center gap-1.5 mt-1">
                                                <Pill tone={p.status === "READY" ? "emerald" : "red"}>
                                                    {p.status}
                                                </Pill>
                                                {p.engine_used && (
                                                    <span className="text-[10px] font-mono text-slate-400">{p.engine_used}</span>
                                                )}
                                                {p.needs_finish_weight && (
                                                    <span
                                                        className="inline-flex items-center gap-0.5 text-[10px] font-semibold text-amber-700 bg-amber-50 border border-amber-300 px-1.5 py-0.5 rounded"
                                                        title="Finish Weight is rule-derived (no drawing weight note found). Editable in the review card."
                                                        data-testid={`needs-finish-weight-${p.id}`}
                                                    >
                                                        <Sparkles className="w-2.5 h-2.5" /> RULE
                                                    </span>
                                                )}
                                            </div>
                                        </button>
                                        <button
                                            onClick={(e) => { e.stopPropagation(); deletePart(p); }}
                                            className="w-full text-[10.5px] text-red-600/70 hover:text-red-700 hover:bg-red-50 py-1 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1"
                                            title="Delete this part entry"
                                            data-testid={`delete-part-${p.id}`}
                                        >
                                            <X className="w-3 h-3" /> Remove part
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </Card>

                    {partDetail ? (
                        <ReviewPanel
                            part={partDetail}
                            qty={qty}
                            setQty={setQty}
                            rmRate={rmRate}
                            setRmRate={setRmRate}
                            scrapRate={scrapRate}
                            setScrapRate={setScrapRate}
                            saving={saving}
                            savedZbcId={savedZbcs[partDetail.id]}
                            onSave={saveAsZBC}
                            customerId={customerId}
                            setCustomerId={setCustomerId}
                            plantId={plantId}
                            setPlantId={setPlantId}
                            customers={customers}
                            plants={plants}
                            onWhy={openWhy}
                            navigate={navigate}
                            opsDraft={opsDraft}
                            opsDirty={opsDirty}
                            opsSaving={opsSaving}
                            updateOpField={updateOpField}
                            deleteOpRow={deleteOpRow}
                            addOpRow={addOpRow}
                            saveOps={saveOps}
                            loadAiSuggestions={loadAiSuggestions}
                            mappings={mappings}
                            editIdentity={editIdentity}
                            setEditIdentity={setEditIdentity}
                            identityDraft={identityDraft}
                            setIdentityDraft={setIdentityDraft}
                            saveIdentity={saveIdentity}
                            editBlank={editBlank}
                            setEditBlank={setEditBlank}
                            blankDraft={blankDraft}
                            setBlankDraft={setBlankDraft}
                            saveBlank={saveBlank}
                            supplierComparison={supplierComparison}
                            supplierBusy={supplierBusy}
                            supplierFileInputRef={supplierFileInputRef}
                            onSupplierUpload={uploadSupplierZbc}
                            onSupplierManual={saveSupplierManual}
                            onSupplierClear={clearSupplierZbc}
                            unitUtilizationPct={unitUtilizationPct}
                        />
                    ) : (
                        <LoadingState />
                    )}
                </div>
            )}

            <WhyModal
                open={whyOpen}
                onClose={() => setWhyOpen(false)}
                title={whyData.title}
                data={whyData.data}
                drawingRef={whyData.drawingRef}
            />
        </div>
    );
}

// -----------------------------------------------------------------------------
function ReviewPanel({
    part, qty, setQty, rmRate, setRmRate, scrapRate, setScrapRate,
    saving, savedZbcId, onSave, onWhy, navigate,
    customerId, setCustomerId, plantId, setPlantId, customers, plants,
    opsDraft, opsDirty, opsSaving, updateOpField, deleteOpRow, addOpRow, saveOps,
    loadAiSuggestions,
    mappings,
    editIdentity, setEditIdentity, identityDraft, setIdentityDraft, saveIdentity,
    editBlank, setEditBlank, blankDraft, setBlankDraft, saveBlank,
    supplierComparison, supplierBusy, supplierFileInputRef,
    onSupplierUpload, onSupplierManual, onSupplierClear,
    unitUtilizationPct = 78,
}) {
    // Master-driven RM/scrap rate selection (requirement #2: "grade-wise rate,
    // we will select from there", instead of typing the number blind). The
    // NumberInputs below stay editable so a one-off negotiated rate can still
    // override the master figure; picking a grade/scrap-code just pre-fills them.
    const [grades, setGrades] = useState([]);
    const [scrapRateRows, setScrapRateRows] = useState([]);
    const [gradeId, setGradeId] = useState("");
    const [scrapRateRowId, setScrapRateRowId] = useState("");

    useEffect(() => {
        api.get("/steel-grades").then((r) => setGrades(r.data)).catch(() => {});
        api.get("/scrap-rates").then((r) => setScrapRateRows(r.data)).catch(() => {});
    }, []);

    useEffect(() => {
        if (!gradeId) return;
        const g = grades.find((x) => x.id === gradeId);
        if (g) setRmRate(String(g.rate_per_kg));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [gradeId]);

    useEffect(() => {
        if (!scrapRateRowId) return;
        const s = scrapRateRows.find((x) => x.id === scrapRateRowId);
        if (s) setScrapRate(String(s.rate_per_kg));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [scrapRateRowId]);
    if (part.status === "FAILED") {
        return (
            <Card>
                <div className="p-5">
                    <ErrorState message={part.error || "Intelligent extraction failed for this drawing."} />
                </div>
            </Card>
        );
    }
    const raw = part.raw || {};
    const identity = raw.identity || {};
    const geometry = raw.geometry || {};
    const features = raw.features || {};
    const blank = part.blank || {};
    const ops = opsDraft ?? part.ops ?? [];
    const totalConversion = ops.reduce((s, op) => s + (parseFloat(op.conversion_cost) || 0), 0);
    const fv = (f) => (f && typeof f === "object" ? f.value : f);

    // Merge live edits over current values for optimistic UI
    const idVal = (k) => (identityDraft[k] !== undefined ? identityDraft[k] : fv(identity[k]) ?? part[k] ?? "");
    const setIdVal = (k, v) => setIdentityDraft({ ...identityDraft, [k]: v });
    const blankVal = (k) => (blankDraft[k] !== undefined ? blankDraft[k] : blank[k] ?? "");
    const setBlankVal = (k, v) => setBlankDraft({ ...blankDraft, [k]: v });

    return (
        <div className="space-y-4">
            {/* Identity: editable */}
            <Card>
                <div className="p-4 flex items-center justify-between border-b border-slate-100">
                    <div className="flex items-center gap-2">
                        <Cpu className="w-4 h-4 text-[hsl(var(--copper))]" />
                        <div className="text-[13px] font-semibold text-slate-800">Identity</div>
                        <Pill tone="slate" className="text-[10px]">
                            {part.source === "MANUAL_ENTRY" ? "Manual"
                                : part.source === "CATALOG_CLONE" ? "Cloned"
                                : "Intelligently extracted"}
                        </Pill>
                    </div>
                    <div className="flex items-center gap-2">
                        <Pill tone="slate">Engine · {part.engine_used}</Pill>
                        {!editIdentity ? (
                            <button
                                onClick={() => setEditIdentity(true)}
                                className="inline-flex items-center gap-1 h-7 px-2.5 text-[11px] rounded-md border border-slate-200 bg-white hover:border-[hsl(var(--copper))] text-slate-700"
                                data-testid="edit-identity-btn"
                            >
                                <Pencil className="w-3 h-3" /> Edit
                            </button>
                        ) : (
                            <>
                                <SlateButton variant="outline" onClick={() => { setEditIdentity(false); setIdentityDraft({}); }} className="h-7 text-[11px]">Cancel</SlateButton>
                                <CopperButton onClick={saveIdentity} className="h-7 text-[11px]" data-testid="save-identity-btn">
                                    Save
                                </CopperButton>
                            </>
                        )}
                    </div>
                </div>
                {!editIdentity ? (
                    <div className="p-4 grid grid-cols-3 gap-3 text-[13px]">
                        <IdRow label="Part Number" field={identity.part_number ?? part.part_number} onWhy={onWhy} />
                        <IdRow label="Part Name" field={identity.part_name ?? part.part_name} onWhy={onWhy} />
                        <IdRow label="Drawing No." field={identity.drawing_number ?? part.drawing_number} onWhy={onWhy} />
                        <IdRow label="Revision" field={identity.revision ?? part.revision} onWhy={onWhy} />
                        <IdRow label="Material" field={identity.material ?? part.material} onWhy={onWhy} />
                        <IdRow label="UOM Detected" field={identity.uom_detected} onWhy={onWhy} />
                    </div>
                ) : (
                    <div className="p-4 grid grid-cols-3 gap-3 text-[13px]">
                        <IdEdit label="Part Number" value={idVal("part_number")} onChange={(v) => setIdVal("part_number", v)} testid="edit-part-number" />
                        <IdEdit label="Part Name" value={idVal("part_name")} onChange={(v) => setIdVal("part_name", v)} testid="edit-part-name" />
                        <IdEdit label="Drawing No." value={idVal("drawing_number")} onChange={(v) => setIdVal("drawing_number", v)} />
                        <IdEdit label="Revision" value={idVal("revision")} onChange={(v) => setIdVal("revision", v)} />
                        <IdEdit label="Material" value={idVal("material")} onChange={(v) => setIdVal("material", v)} />
                        <IdEdit label="UOM Detected" value={idVal("uom_detected")} onChange={(v) => setIdVal("uom_detected", v)} />
                    </div>
                )}
            </Card>

            {/* Blank + geometry: editable */}
            <Card>
                {part.finish_weight_cross_check && (() => {
                    const c = part.finish_weight_cross_check;
                    const highSeverity = c.severity === "HIGH";
                    const rowCls = highSeverity
                        ? "border-b border-rose-300 bg-rose-50 text-rose-900"
                        : "border-b border-amber-300 bg-amber-50 text-amber-900";
                    const icoCls = highSeverity ? "text-rose-600" : "text-amber-600";
                    return (
                        <div
                            className={`${rowCls} px-4 py-2.5 flex items-start gap-2`}
                            data-testid="ai-finish-weight-cross-check"
                        >
                            <AlertTriangle className={`mt-0.5 h-4 w-4 flex-shrink-0 ${icoCls}`} />
                            <div className="text-[12px] leading-snug">
                                <div className="font-semibold">
                                    Finish Weight cross-check · Δ {c.delta_pct > 0 ? "+" : ""}{c.delta_pct}% ({c.delta_kg > 0 ? "+" : ""}{c.delta_kg} kg)
                                </div>
                                <div className="mt-0.5">
                                    Drawing note (Intelligent): <span className="font-mono font-semibold">{c.ai_value_kg} kg</span>{" "}
                                    · Shape rule ({c.rule_shape} loss {c.rule_loss_pct}%): <span className="font-mono font-semibold">{c.rule_value_kg} kg</span>
                                </div>
                                <div className="opacity-80 mt-0.5">
                                    {highSeverity
                                        ? "Large gap: please verify which value is correct before saving."
                                        : "Moderate gap: the drawing note is usually more accurate."}
                                </div>
                            </div>
                        </div>
                    );
                })()}
                {part.needs_finish_weight && (
                    <div
                        className="border-b border-amber-200 bg-amber-50/70 px-4 py-2 flex items-start gap-2"
                        data-testid="ai-needs-finish-weight-banner"
                    >
                        <Sparkles className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-amber-600" />
                        <div className="text-[11.5px] text-amber-900 leading-snug">
                            <span className="font-semibold">Finish Weight is rule-derived.</span>
                            {" "}
                            The Intelligent Wizard could not locate a &ldquo;Weight&rdquo; note in the drawing&apos;s title block, so CostIQ used a shape-specific machining loss % from the Formula Registry. Click <span className="font-mono">Edit</span> to override with the exact drawing value if you have it.
                        </div>
                    </div>
                )}
                <div className="p-4 border-b border-slate-100 flex items-center justify-between">
                    <div className="text-[13px] font-semibold text-slate-800">Blank & Geometry</div>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => onWhy("Blank Weight", blank, part.filename)}
                            className="inline-flex items-center gap-1 text-[11px] text-[hsl(var(--copper))] hover:underline"
                            data-testid="why-blank"
                        >
                            <Lightbulb className="w-3 h-3" /> Why?
                        </button>
                        {!editBlank ? (
                            <button
                                onClick={() => setEditBlank(true)}
                                className="inline-flex items-center gap-1 h-7 px-2.5 text-[11px] rounded-md border border-slate-200 bg-white hover:border-[hsl(var(--copper))] text-slate-700"
                                data-testid="edit-blank-btn"
                            >
                                <Pencil className="w-3 h-3" /> Edit
                            </button>
                        ) : (
                            <>
                                <SlateButton variant="outline" onClick={() => { setEditBlank(false); setBlankDraft({}); }} className="h-7 text-[11px]">Cancel</SlateButton>
                                <CopperButton onClick={saveBlank} className="h-7 text-[11px]" data-testid="save-blank-btn">Save</CopperButton>
                            </>
                        )}
                    </div>
                </div>
                {blank.shape_auto_corrected && (
                    <div
                        className="border-b border-amber-300 bg-amber-50 px-4 py-2.5 flex items-start gap-2"
                        data-testid="ai-shape-corrected-banner"
                    >
                        <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-600" />
                        <div className="text-[12px] text-amber-900">
                            <div className="font-semibold">Shape auto-corrected from {blank.shape_original} → {blank.raw_material_type}</div>
                            <div className="text-amber-800/90 leading-snug mt-0.5">
                                {blank.shape_correction_reason} If the correction is wrong, click <span className="font-mono">Edit</span> and set the Raw Material field manually.
                            </div>
                        </div>
                    </div>
                )}
                {!editBlank ? (
                    <div className="p-4 grid grid-cols-4 gap-3 text-[13px]">
                        <GeomCell label="Raw Material" v={blank.raw_material_type} />
                        <GeomCell label="Blank Weight (kg)" v={blank.blank_weight_kg} />
                        <GeomCell label="Density (kg/m³)" v={blank.density_kg_m3} />
                        <GeomCell label="Shape" v={fv(geometry.shape_family)} />
                        {blank.blank_length_mm !== undefined && <GeomCell label="L blank (mm)" v={blank.blank_length_mm} />}
                        {blank.blank_width_mm !== undefined && <GeomCell label="W blank (mm)" v={blank.blank_width_mm} />}
                        {blank.blank_thickness_mm !== undefined && <GeomCell label="T (mm)" v={blank.blank_thickness_mm} />}
                        {blank.blank_diameter_mm !== undefined && <GeomCell label="Ø (mm)" v={blank.blank_diameter_mm} />}
                        {blank.blank_across_flats_mm !== undefined && <GeomCell label="A/F (mm)" v={blank.blank_across_flats_mm} />}
                        <GeomCell
                            label="Finish Weight (kg)"
                            v={fv(geometry.finish_weight_kg) ?? (part.suggested_finish_weight?.value)}
                            className={part.needs_finish_weight ? "bg-rose-50 border border-rose-300 rounded px-2 py-1" : ""}
                            hint={
                                geometry.finish_weight_kg == null && part.suggested_finish_weight
                                    ? `${part.suggested_finish_weight.source} · ${part.suggested_finish_weight.confidence}${part.suggested_finish_weight.loss_pct ? ` · −${part.suggested_finish_weight.loss_pct}%` : ""}`
                                    : null
                            }
                        />
                    </div>
                ) : (
                    <div className="p-4 grid grid-cols-4 gap-3 text-[13px]">
                        <BlankEdit label="Raw Material" value={blankVal("raw_material_type")} onChange={(v) => setBlankVal("raw_material_type", v)} />
                        <BlankEdit label="Blank Weight (kg)" value={blankVal("blank_weight_kg")} onChange={(v) => setBlankVal("blank_weight_kg", v)} type="number" step="0.001" testid="edit-blank-weight" />
                        <BlankEdit label="Density (kg/m³)" value={blankVal("density_kg_m3")} onChange={(v) => setBlankVal("density_kg_m3", v)} type="number" />
                        <BlankEdit label="L blank (mm)" value={blankVal("blank_length_mm")} onChange={(v) => setBlankVal("blank_length_mm", v)} type="number" />
                        <BlankEdit label="W blank (mm)" value={blankVal("blank_width_mm")} onChange={(v) => setBlankVal("blank_width_mm", v)} type="number" />
                        <BlankEdit label="T (mm)" value={blankVal("blank_thickness_mm")} onChange={(v) => setBlankVal("blank_thickness_mm", v)} type="number" step="0.1" />
                        <BlankEdit label="Ø (mm)" value={blankVal("blank_diameter_mm")} onChange={(v) => setBlankVal("blank_diameter_mm", v)} type="number" />
                        <BlankEdit label="A/F (mm)" value={blankVal("blank_across_flats_mm")} onChange={(v) => setBlankVal("blank_across_flats_mm", v)} type="number" />
                        <BlankEdit
                            label="Finish Weight (kg) *"
                            value={blankDraft.finish_weight_kg !== undefined ? blankDraft.finish_weight_kg : (fv(geometry.finish_weight_kg) ?? (part.suggested_finish_weight?.value ?? ""))}
                            onChange={(v) => setBlankVal("finish_weight_kg", v)}
                            type="number"
                            step="0.001"
                            testid="edit-finish-weight"
                        />
                    </div>
                )}
                <div className="px-4 pb-3 text-[11.5px] text-slate-500 italic">{blank.explanation}</div>
            </Card>

            {/* Features */}
            {(features.holes?.length || features.threads?.length || features.chamfers?.length ||
                features.welding_symbols?.length) && (
                <Card>
                    <div className="p-4 border-b border-slate-100 text-[13px] font-semibold text-slate-800">Features</div>
                    <div className="p-4 grid grid-cols-2 gap-3 text-[12.5px]">
                        {features.holes?.length > 0 && (
                            <FeatureRow label="Holes" items={features.holes.map(h => `Ø${h.dia_mm} × ${h.qty}`)} />
                        )}
                        {features.threads?.length > 0 && (
                            <FeatureRow label="Threads" items={features.threads.map(t => `${t.spec} × ${t.qty}`)} />
                        )}
                        {features.chamfers?.length > 0 && (
                            <FeatureRow label="Chamfers" items={features.chamfers.map(c => `${c.size_mm}×${c.angle_deg}° ×${c.qty}`)} />
                        )}
                        {features.welding_symbols?.length > 0 && (
                            <FeatureRow label="Welding" items={features.welding_symbols} />
                        )}
                        {features.surface_finish && (
                            <FeatureRow label="Surface Finish" items={[fv(features.surface_finish)]} />
                        )}
                        {features.gd_and_t?.length > 0 && (
                            <FeatureRow label="GD&T" items={features.gd_and_t} />
                        )}
                    </div>
                </Card>
            )}

            {/* Route + Ops (editable) */}
            <Card>
                <div className="p-4 border-b border-slate-100 flex items-center justify-between">
                    <div className="text-[13px] font-semibold text-slate-800">Manufacturing Route & Conversion Cost</div>
                    <div className="flex items-center gap-3">
                        <div className="text-[12px] font-mono">Total: <span className="text-[hsl(var(--copper))] font-semibold">{fmtINR(totalConversion, 2)}</span></div>
                        {loadAiSuggestions && (
                            <button
                                onClick={loadAiSuggestions}
                                className="inline-flex items-center gap-1 h-7 px-2.5 text-[11px] rounded-md border border-slate-200 bg-white hover:bg-amber-50 hover:border-[hsl(var(--copper))] text-slate-700"
                                data-testid="load-ai-suggestions"
                                title="Populate the table with intelligently-inferred operations"
                            >
                                <Sparkles className="w-3 h-3" /> Load Intelligent Suggestions
                            </button>
                        )}
                        <button
                            onClick={addOpRow}
                            className="inline-flex items-center gap-1 h-7 px-2.5 text-[11px] rounded-md border border-slate-200 bg-white hover:bg-slate-50 hover:border-[hsl(var(--copper))] text-slate-700"
                            data-testid="add-op-row"
                        >
                            + Add row
                        </button>
                        {opsDirty && (
                            <CopperButton onClick={saveOps} disabled={opsSaving} data-testid="save-ops-btn" className="h-7 text-[11px]">
                                {opsSaving ? "Saving…" : "Save changes"}
                            </CopperButton>
                        )}
                    </div>
                </div>
                <div className="overflow-x-auto">
                    <datalist id="ops-master-list">
                        {(mappings || []).map((m) => (
                            <option key={m.id} value={m.operation_name}>
                                {m.category === "MACHINE" ? m.mapped_machine : m.mapped_process} · {m.uom} · ₹{m.standard_rate}
                            </option>
                        ))}
                    </datalist>
                    <table className="w-full text-[12.5px]">
                        <thead className="bg-slate-50/60 border-b border-slate-200">
                            <tr>
                                <th className="text-left py-2 px-3 eyebrow text-slate-500">Op</th>
                                <th className="text-left py-2 px-3 eyebrow text-slate-500">Machine</th>
                                <th className="text-right py-2 px-3 eyebrow text-slate-500">Rate/hr</th>
                                <th className="text-right py-2 px-3 eyebrow text-slate-500">Cycle Time (s)</th>
                                <th className="text-right py-2 px-3 eyebrow text-slate-500">Parts/hr</th>
                                <th className="text-right py-2 px-3 eyebrow text-slate-500">Cost</th>
                                <th className="py-2 px-3 eyebrow text-slate-500 w-16 text-center">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {ops.map((op, i) => (
                                <tr key={op._rowId ?? i} className="border-b border-slate-100 hover:bg-slate-50/30">
                                    <td className="py-1.5 px-3">
                                        <div className="flex items-center gap-1.5">
                                            <input
                                                list={`ops-master-list`}
                                                className="flex-1 min-w-[140px] px-2 py-1 text-[12.5px] border border-transparent hover:border-slate-200 focus:border-[hsl(var(--copper))] focus:outline-none rounded bg-transparent"
                                                value={op.op || ""}
                                                placeholder="Pick from master or type…"
                                                onChange={(e) => updateOpField(i, "op", e.target.value)}
                                                data-testid={`op-name-${i}`}
                                            />
                                            {op.source === "ai" && (
                                                <span
                                                    className="inline-flex items-center gap-0.5 text-[9px] font-semibold text-[hsl(var(--copper))] bg-[hsl(var(--copper-soft))]/40 border border-[hsl(var(--copper))]/30 px-1 py-0.5 rounded whitespace-nowrap"
                                                    title={`Claude inferred this op from drawing features${op.ai_confidence ? ` · confidence ${(op.ai_confidence * 100).toFixed(0)}%` : ""}`}
                                                    data-testid={`op-source-ai-${i}`}
                                                >
                                                    <Sparkles className="w-2.5 h-2.5" /> Auto
                                                </span>
                                            )}
                                        </div>
                                    </td>
                                    <td className="py-1.5 px-3">
                                        <input
                                            className="w-24 px-2 py-1 text-[12px] font-mono border border-transparent hover:border-slate-200 focus:border-[hsl(var(--copper))] focus:outline-none rounded bg-transparent"
                                            value={op.machine_code || ""}
                                            onChange={(e) => updateOpField(i, "machine_code", e.target.value)}
                                            placeholder="—"
                                        />
                                    </td>
                                    <td className="py-1.5 px-3 text-right">
                                        <input
                                            type="number" step="1"
                                            className="w-20 px-2 py-1 text-right font-mono text-[12.5px] border border-transparent hover:border-slate-200 focus:border-[hsl(var(--copper))] focus:outline-none rounded bg-transparent"
                                            value={op.machine_hour_rate ?? ""}
                                            placeholder="—"
                                            onChange={(e) => updateOpField(i, "machine_hour_rate", e.target.value)}
                                            data-testid={`op-rate-${i}`}
                                        />
                                    </td>
                                    <td className="py-1.5 px-3 text-right">
                                        <input
                                            type="number" step="1"
                                            className="w-16 px-2 py-1 text-right font-mono text-[12.5px] border border-transparent hover:border-slate-200 focus:border-[hsl(var(--copper))] focus:outline-none rounded bg-transparent"
                                            value={op.cycle_time_seconds ?? ""}
                                            placeholder="—"
                                            onChange={(e) => updateOpField(i, "cycle_time_seconds", e.target.value)}
                                            data-testid={`op-cycle-${i}`}
                                        />
                                    </td>
                                    <td className="py-1.5 px-3 text-right font-mono tabular-nums text-slate-500">{op.productivity_pph ?? 0}</td>
                                    <td className="py-1.5 px-3 text-right font-mono tabular-nums font-semibold">{fmtINR(op.conversion_cost || 0, 2)}</td>
                                    <td className="py-1.5 px-3 text-center">
                                        <div className="inline-flex items-center gap-1">
                                            {op.explanation && (
                                                <button
                                                    onClick={() => onWhy(`${op.op}: Why?`, op, part.filename)}
                                                    className="p-1 rounded hover:bg-slate-100 text-[hsl(var(--copper))]"
                                                    title="Why?"
                                                >
                                                    <Lightbulb className="w-3.5 h-3.5" />
                                                </button>
                                            )}
                                            <button
                                                onClick={() => deleteOpRow(i)}
                                                className="p-1 rounded hover:bg-red-50 text-red-500"
                                                title="Delete row"
                                                data-testid={`delete-op-${i}`}
                                            >
                                                <X className="w-3.5 h-3.5" />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                            {ops.length === 0 && (
                                <tr>
                                    <td colSpan={7} className="py-6 text-center text-[12px] text-slate-500">
                                        No conversion operations. Click <span className="font-semibold">+ Add row</span> to add one.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </Card>

            {/* GAP 5 · Unit vs Corp Cycle-Time Twin View */}
            {ops.length > 0 && totalConversion > 0 && (
                <Card>
                    <div className="p-4 border-b border-slate-100 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <TrendingUp className="w-4 h-4 text-[hsl(var(--copper))]" />
                            <div className="text-[13px] font-semibold text-slate-800">Unit vs Corp Costing View</div>
                            <span className="text-[10.5px] text-slate-500 italic">DemoClient two-school analysis · Utilisation {unitUtilizationPct}%</span>
                        </div>
                    </div>
                    <div className="p-4">
                        {(() => {
                            const util = Math.max(1, parseFloat(unitUtilizationPct) || 78) / 100;
                            const corpConv = totalConversion;
                            const unitConv = corpConv / util;
                            const delta = unitConv - corpConv;
                            const monthly = delta * (parseFloat(qty) || 0);
                            return (
                                <div className="grid grid-cols-3 gap-4 text-[13px]">
                                    <div className="rounded-md border border-slate-200 p-3 bg-white">
                                        <div className="text-[10.5px] uppercase tracking-widest text-slate-500 font-semibold flex items-center">
                                            Corp WK (Engineered)
                                            <InfoTip title="Corp WK (Engineered)">
                                                The theoretical conversion cost if the machine ran at <strong>100% utilisation</strong>: no setup, no changeovers, no OEE losses.{"\n\n"}
                                                <strong>Calculated as:</strong> the sum of every operation's (cycle time × machine ₹/hr) from the Conversion Cost table above.{"\n\n"}
                                                This is what the engineering drawing physically demands, not what a real shop floor achieves.
                                            </InfoTip>
                                        </div>
                                        <div className="text-[10.5px] text-slate-500 mt-0.5">100% utilisation · cycle time from drawing</div>
                                        <div className="text-[18px] font-mono tabular-nums font-bold text-slate-900 mt-2" data-testid="twin-corp-conversion">
                                            {fmtINR(corpConv, 2)}
                                        </div>
                                        <div className="text-[11px] text-slate-500 mt-0.5">per piece · sum of ops above</div>
                                    </div>
                                    <div className="rounded-md border border-amber-200 p-3 bg-amber-50/60">
                                        <div className="text-[10.5px] uppercase tracking-widest text-amber-800 font-semibold flex items-center">
                                            Unit WK (Realistic)
                                            <InfoTip title="Unit WK (Realistic)">
                                                The same conversion cost adjusted for <strong>real-world plant losses</strong> (setup time, changeovers, and OEE) using the Unit WK Utilisation % (default 78%, editable in Formula Registry).{"\n\n"}
                                                <strong>Calculated as:</strong> Corp WK ÷ Utilisation %.{"\n\n"}
                                                This is closer to what a live production line actually costs per piece.
                                            </InfoTip>
                                        </div>
                                        <div className="text-[10.5px] text-amber-700 mt-0.5">{unitUtilizationPct}% utilisation · OEE + setup + changeovers</div>
                                        <div className="text-[18px] font-mono tabular-nums font-bold text-amber-900 mt-2" data-testid="twin-unit-conversion">
                                            {fmtINR(unitConv, 2)}
                                        </div>
                                        <div className="text-[11px] text-amber-700 mt-0.5">per piece · engineered ÷ {unitUtilizationPct}%</div>
                                    </div>
                                    <div className="rounded-md border border-rose-200 p-3 bg-rose-50/60">
                                        <div className="text-[10.5px] uppercase tracking-widest text-rose-800 font-semibold flex items-center">
                                            Δ Unit − Corp
                                            <InfoTip title="Δ Unit − Corp" side="left">
                                                The gap between the realistic (Unit) and engineered (Corp) view, per piece and per month at the entered quantity.{"\n\n"}
                                                <strong>Calculated as:</strong> Unit WK − Corp WK, × Quantity for the monthly figure.{"\n\n"}
                                                Procurement quotes typically land somewhere between Corp and Unit: this shows exactly how much room there is to negotiate within.
                                            </InfoTip>
                                        </div>
                                        <div className="text-[10.5px] text-rose-700 mt-0.5">If quoted at Unit view</div>
                                        <div className="text-[18px] font-mono tabular-nums font-bold text-rose-900 mt-2" data-testid="twin-delta">
                                            +{fmtINR(delta, 2)}
                                        </div>
                                        <div className="text-[11px] text-rose-700 mt-0.5">
                                            per piece · <span className="font-semibold">+{fmtINR(monthly, 2)}/mo</span> at {qty} pcs
                                        </div>
                                    </div>
                                </div>
                            );
                        })()}
                        <div className="mt-3 text-[11px] text-slate-500 leading-snug">
                            <span className="font-semibold text-slate-700">How to read:</span> The engineered Corp view is what the drawing physically demands. The Unit view models real-world losses. Procurement quotes typically land between these two; use both to negotiate. Adjust <span className="font-mono">Unit WK Utilisation %</span> in <a href="/formulas" className="text-[hsl(var(--copper))] hover:underline">Formula Registry</a>.
                        </div>
                    </div>
                </Card>
            )}

            {/* Supplier ZBC Comparison */}
            <SupplierZBCCard
                part={part}
                comparison={supplierComparison}
                busy={supplierBusy}
                fileInputRef={supplierFileInputRef}
                onUpload={onSupplierUpload}
                onManual={onSupplierManual}
                onClear={onSupplierClear}
            />

            {/* User inputs → Save */}
            <Card>
                <div className="p-4 border-b border-slate-100 text-[13px] font-semibold text-slate-800">
                    Your Inputs · Save as ZBC
                </div>

                {part.customer_id && part.plant_id ? (
                    <div className="px-4 pt-4 text-[12px] text-slate-500" data-testid="ai-customer-plant-readonly">
                        Customer <span className="font-medium text-slate-700">{customers.find((c) => c.id === part.customer_id)?.name || "—"}</span>
                        {" · "}Plant <span className="font-medium text-slate-700">{plants.find((p) => p.id === part.plant_id)?.code || "—"}</span>
                    </div>
                ) : (
                    <div className="mx-4 mt-4 p-3 rounded-md border border-amber-200 bg-amber-50/60" data-testid="ai-customer-plant-picker">
                        <div className="text-[11px] font-semibold text-amber-800 uppercase tracking-wide mb-2">
                            Customer &amp; Plant weren&rsquo;t set before analysing: pick them here to save
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <Field label="Customer">
                                <Select value={customerId} onChange={(e) => setCustomerId(e.target.value)} data-testid="ai-review-customer">
                                    <option value="">Select customer…</option>
                                    {customers.map((c) => <option key={c.id} value={c.id}>{c.code} · {c.name}</option>)}
                                </Select>
                            </Field>
                            <Field label="Plant">
                                <Select value={plantId} onChange={(e) => setPlantId(e.target.value)} data-testid="ai-review-plant">
                                    <option value="">Select plant…</option>
                                    {plants.filter((p) => p.is_active !== false).map((p) => <option key={p.id} value={p.id}>{p.code} · {p.name}</option>)}
                                </Select>
                            </Field>
                        </div>
                    </div>
                )}

                <div className="p-4 grid grid-cols-3 gap-3">
                    <Field
                        label={
                            <span className="inline-flex items-center gap-1.5">
                                Quantity
                                {part.ai_qty_suggestion && parseFloat(qty) === parseFloat(part.ai_qty_suggestion) && (
                                    <span
                                        className="inline-flex items-center gap-1 text-[9.5px] font-semibold text-[hsl(var(--copper))] bg-[hsl(var(--copper-soft))]/40 border border-[hsl(var(--copper))]/30 px-1.5 py-0.5 rounded"
                                        title={`Claude extracted "${part.ai_qty_suggestion}" from the drawing title block`}
                                        data-testid="ai-qty-chip"
                                    >
                                        <Sparkles className="w-2.5 h-2.5" /> Auto · title block
                                    </span>
                                )}
                            </span>
                        }
                    >
                        <NumberInput value={qty} onChange={(e) => setQty(e.target.value)} step="1" min="1" data-testid="ai-qty" />
                    </Field>
                    <Field label="Steel Grade" helper="Auto-fills RM Rate below, editable after">
                        <Select value={gradeId} onChange={(e) => setGradeId(e.target.value)} data-testid="ai-steel-grade">
                            <option value="">Select grade…</option>
                            {grades.filter((g) => g.is_active).map((g) => <option key={g.id} value={g.id}>{g.code} · ₹{g.rate_per_kg}/kg</option>)}
                        </Select>
                    </Field>
                    <Field label="Scrap Rate Code" helper="Auto-fills Scrap Rate below, editable after">
                        <Select value={scrapRateRowId} onChange={(e) => setScrapRateRowId(e.target.value)} data-testid="ai-scrap-rate-code">
                            <option value="">Select scrap rate…</option>
                            {scrapRateRows.filter((s) => s.is_active).map((s) => <option key={s.id} value={s.id}>{s.code} · ₹{s.rate_per_kg}/kg</option>)}
                        </Select>
                    </Field>
                    <Field label="RM Rate ₹/kg" required>
                        <NumberInput value={rmRate} onChange={(e) => setRmRate(e.target.value)} step="0.01" placeholder="e.g. 54" data-testid="ai-rmrate" />
                    </Field>
                    <Field label="Scrap Rate ₹/kg">
                        <NumberInput value={scrapRate} onChange={(e) => setScrapRate(e.target.value)} step="0.01" placeholder="e.g. 28" data-testid="ai-scraprate" />
                    </Field>
                    <div className="flex items-end">
                        {savedZbcId ? (
                            <SlateButton
                                variant="outline"
                                onClick={() => navigate(`/zbc/${savedZbcId}`)}
                                data-testid="ai-view-zbc"
                            >
                                <CheckCircle2 className="w-4 h-4" /> View saved ZBC
                            </SlateButton>
                        ) : (
                            <CopperButton
                                onClick={() => onSave()}
                                disabled={saving || !rmRate || !(part.customer_id || customerId) || !(part.plant_id || plantId)}
                                title={!(part.customer_id || customerId) || !(part.plant_id || plantId) ? "Pick Customer & Plant above first" : undefined}
                                data-testid="ai-save-zbc"
                            >
                                {saving ? "Saving…" : <>Save as ZBC <ArrowRight className="w-4 h-4" /></>}
                            </CopperButton>
                        )}
                    </div>
                </div>
            </Card>
        </div>
    );
}

// ----------------- Small subcomponents -----------------
function StepDot({ n, label, active, done }) {
    return (
        <div className="flex items-center gap-2">
            <div className={`w-7 h-7 rounded-full text-[12px] font-semibold flex items-center justify-center border-2 ${
                done ? "bg-emerald-500 border-emerald-500 text-white"
                     : active ? "bg-[hsl(var(--copper))] border-[hsl(var(--copper))] text-white"
                              : "bg-white border-slate-300 text-slate-400"
            }`}>{done ? "✓" : n}</div>
            <div className={`text-[12px] font-medium ${active ? "text-slate-800" : "text-slate-500"}`}>{label}</div>
        </div>
    );
}
function StepArrow() { return <div className="flex-1 h-px bg-slate-200 mx-1" />; }
function IdRow({ label, field, onWhy }) {
    if (!field) return <div className="text-[12px] text-slate-400">{label}: —</div>;
    const v = typeof field === "object" ? field.value : field;
    const c = typeof field === "object" ? field.confidence : null;
    return (
        <div>
            <div className="text-[10.5px] uppercase tracking-widest text-slate-500 font-semibold mb-0.5 flex items-center gap-1">
                {label}
                {c != null && (
                    <button
                        onClick={() => onWhy(label, { value: v, confidence: c }, null)}
                        className="opacity-50 hover:opacity-100"
                        title="Why?"
                    >
                        <HelpCircle className="w-3 h-3" />
                    </button>
                )}
            </div>
            <div className="text-[13px] font-medium text-slate-800 truncate">{v || "—"}</div>
        </div>
    );
}
function GeomCell({ label, v, className = "", hint = null }) {
    return (
        <div className={className}>
            <div className="text-[10.5px] uppercase tracking-widest text-slate-500 font-semibold mb-0.5">{label}</div>
            <div className="text-[13px] font-mono tabular-nums text-slate-800">{v ?? "—"}</div>
            {hint && (
                <div className="mt-0.5 text-[10px] text-amber-700 font-medium tracking-wide" data-testid="geom-cell-hint">{hint}</div>
            )}
        </div>
    );
}
function FeatureRow({ label, items }) {
    return (
        <div className="space-y-1">
            <div className="text-[10.5px] uppercase tracking-widest text-slate-500 font-semibold">{label}</div>
            <div className="flex flex-wrap gap-1">
                {items.map((it, i) => (
                    <span key={i} className="inline-flex items-center px-2 py-0.5 rounded bg-slate-100 border border-slate-200 text-[11px] font-mono">{it}</span>
                ))}
            </div>
        </div>
    );
}


// ------------------ New sub-components (Session 10) ------------------
function ModeTab({ active, onClick, icon: Icon, label, testid }) {
    return (
        <button
            onClick={onClick}
            data-testid={testid}
            className={`inline-flex items-center gap-2 px-4 py-2.5 -mb-px text-[13px] font-medium border-b-2 transition-colors ${
                active
                    ? "border-[hsl(var(--copper))] text-[hsl(var(--copper))]"
                    : "border-transparent text-slate-500 hover:text-slate-800 hover:border-slate-300"
            }`}
        >
            <Icon className="w-4 h-4" />
            {label}
        </button>
    );
}

function SummaryStat({ label, value }) {
    return (
        <div>
            <div className="text-[10.5px] uppercase tracking-widest text-slate-500 font-semibold">{label}</div>
            <div className="text-[15px] font-mono tabular-nums font-semibold text-slate-800">{value}</div>
        </div>
    );
}

function IdEdit({ label, value, onChange, testid }) {
    return (
        <div>
            <div className="text-[10.5px] uppercase tracking-widest text-slate-500 font-semibold mb-0.5">{label}</div>
            <TextInput value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={testid} />
        </div>
    );
}

function BlankEdit({ label, value, onChange, type = "text", step, testid }) {
    return (
        <div>
            <div className="text-[10.5px] uppercase tracking-widest text-slate-500 font-semibold mb-0.5">{label}</div>
            {type === "number" ? (
                <NumberInput value={value ?? ""} step={step || "0.01"} onChange={(e) => onChange(e.target.value)} data-testid={testid} />
            ) : (
                <TextInput value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={testid} />
            )}
        </div>
    );
}

function ManualEntryForm({ form, setForm, onSubmit, submitting }) {
    const upd = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));
    return (
        <div className="space-y-4" data-testid="manual-entry-form">
            <div className="text-[12px] text-slate-500">
                Fill the drawing details manually. The same deterministic engines that process intelligently-extracted PDFs will
                infer raw material size, manufacturing route and cycle times.
            </div>
            <div className="grid grid-cols-4 gap-3">
                <Field label="Part Number *">
                    <TextInput value={form.part_number} onChange={(e) => upd("part_number", e.target.value)} data-testid="manual-part-number" />
                </Field>
                <Field label="Part Name">
                    <TextInput value={form.part_name} onChange={(e) => upd("part_name", e.target.value)} />
                </Field>
                <Field label="Drawing Number">
                    <TextInput value={form.drawing_number} onChange={(e) => upd("drawing_number", e.target.value)} />
                </Field>
                <Field label="Material">
                    <Select value={form.material} onChange={(e) => upd("material", e.target.value)}>
                        <option>MILD_STEEL</option>
                        <option>SS304</option>
                        <option>SS316</option>
                        <option>ALUMINIUM</option>
                        <option>CAST_IRON</option>
                    </Select>
                </Field>
                <Field label="RM Shape">
                    <Select value={form.shape_family} onChange={(e) => upd("shape_family", e.target.value)}>
                        <option value="PLATE">Plate</option>
                        <option value="ROUND_BAR">Round Bar</option>
                        <option value="SQUARE_TUBE">Square Tube</option>
                    </Select>
                </Field>
                <Field label="Length (mm)">
                    <NumberInput value={form.length_mm} onChange={(e) => upd("length_mm", e.target.value)} />
                </Field>
                <Field label="Width (mm)">
                    <NumberInput value={form.width_mm} onChange={(e) => upd("width_mm", e.target.value)} />
                </Field>
                <Field label="Thickness (mm)">
                    <NumberInput value={form.thickness_mm} onChange={(e) => upd("thickness_mm", e.target.value)} step="0.1" />
                </Field>
                <Field label="Diameter (mm)">
                    <NumberInput value={form.diameter_mm} onChange={(e) => upd("diameter_mm", e.target.value)} />
                </Field>
                <Field label="Height (mm)">
                    <NumberInput value={form.height_mm} onChange={(e) => upd("height_mm", e.target.value)} />
                </Field>
                <Field label="# Holes">
                    <NumberInput value={form.holes_count} onChange={(e) => upd("holes_count", e.target.value)} min="0" step="1" />
                </Field>
                <Field label="# Threads">
                    <NumberInput value={form.threads_count} onChange={(e) => upd("threads_count", e.target.value)} min="0" step="1" />
                </Field>
                <Field label="# Chamfers">
                    <NumberInput value={form.chamfers_count} onChange={(e) => upd("chamfers_count", e.target.value)} min="0" step="1" />
                </Field>
                <Field label="Welding required">
                    <Select value={String(form.has_welding)} onChange={(e) => upd("has_welding", e.target.value === "true")}>
                        <option value="false">No</option>
                        <option value="true">Yes</option>
                    </Select>
                </Field>
            </div>
            <div className="flex justify-end">
                <CopperButton onClick={onSubmit} disabled={submitting} data-testid="manual-submit-btn">
                    {submitting ? "Creating…" : <>Create Part <ArrowRight className="w-4 h-4" /></>}
                </CopperButton>
            </div>
        </div>
    );
}

function CatalogPanel({ query, setQuery, results, loading, onSearch, onPick }) {
    return (
        <div className="space-y-3" data-testid="catalog-panel">
            <div className="flex items-center gap-2">
                <div className="relative flex-1">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input
                        className="w-full h-10 pl-9 pr-3 rounded-md border border-slate-200 focus:border-[hsl(var(--copper))] outline-none text-[13px]"
                        placeholder="Search saved parts by number, name or drawing…"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && onSearch()}
                        data-testid="catalog-search-input"
                    />
                </div>
                <CopperButton onClick={onSearch} disabled={loading} data-testid="catalog-search-btn">
                    {loading ? "Searching…" : "Search"}
                </CopperButton>
            </div>
            {results.length === 0 ? (
                <div className="text-[12.5px] text-slate-500 border border-dashed border-slate-200 rounded p-6 text-center">
                    {loading ? "Loading…" : "No matches yet. Try searching by part number, part name or drawing number."}
                </div>
            ) : (
                <div className="max-h-96 overflow-y-auto border border-slate-200 rounded">
                    <table className="w-full text-[12.5px]">
                        <thead className="sticky top-0 bg-slate-50 border-b border-slate-200">
                            <tr>
                                <th className="text-left py-2 px-3 eyebrow text-slate-500">Part No</th>
                                <th className="text-left py-2 px-3 eyebrow text-slate-500">Part Name</th>
                                <th className="text-left py-2 px-3 eyebrow text-slate-500">Drawing</th>
                                <th className="text-right py-2 px-3 eyebrow text-slate-500">Should-Cost</th>
                                <th className="py-2 px-3 w-24"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {results.map((r) => (
                                <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50/60" data-testid={`catalog-row-${r.id}`}>
                                    <td className="py-1.5 px-3 font-mono font-semibold">{r.part_number || "—"}</td>
                                    <td className="py-1.5 px-3">{r.part_name || "—"}</td>
                                    <td className="py-1.5 px-3 font-mono text-slate-500">{r.drawing_number || "—"}</td>
                                    <td className="py-1.5 px-3 text-right font-mono tabular-nums">₹{r.should_cost?.toLocaleString?.() ?? r.should_cost}</td>
                                    <td className="py-1.5 px-3 text-right">
                                        <button
                                            onClick={() => onPick(r.id)}
                                            className="inline-flex items-center gap-1 h-7 px-2.5 text-[11px] rounded-md border border-slate-200 bg-white hover:border-[hsl(var(--copper))] text-slate-700"
                                            data-testid={`catalog-use-${r.id}`}
                                        >
                                            Use
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}


// ---------- Supplier ZBC comparison card ----------
// input_wt / finish_wt / rm_rate get their own row above (rm_rate is
// steel-grade-driven, see WEIGHT_RATE_FIELDS below); they're what powers the
// Variance Breakup drill-down's weight-vs-steel-rate split on ZBC Detail.
const WEIGHT_RATE_FIELDS = [
    { key: "input_wt",  label: "Input Weight (kg)" },
    { key: "finish_wt", label: "Finish Weight (kg)" },
];
const MANUAL_FIELDS = [
    { key: "net_material",  label: "Net Material Cost (₹)" },
    { key: "conversion",    label: "Conversion Cost (₹)" },
    { key: "sub_total",     label: "Sub Total (₹)" },
    { key: "icc",           label: "ICC (₹)" },
    { key: "tool_maint",    label: "Tool Maintenance (₹)" },
    { key: "rejection",     label: "Rejection (₹)" },
    { key: "packing_fwd",   label: "Packing & Forwarding (₹)" },
    { key: "profit",        label: "Profit (₹)" },
    { key: "overhead",      label: "Overhead (₹)" },
    { key: "tpt",           label: "TPT / Freight (₹)" },
    { key: "net_price",     label: "Net Price (₹)" },
    { key: "monthly_volume",label: "Monthly Volume (nos)" },
];

function SupplierZBCCard({ part, comparison, busy, fileInputRef, onUpload, onManual, onClear }) {
    const [mode, setMode] = useState(null); // null | "manual"
    const [suppliers, setSuppliers] = useState([]);
    const [supplierId, setSupplierId] = useState("");
    const [grades, setGrades] = useState([]);
    const [gradeId, setGradeId] = useState("");
    const [manualDraft, setManualDraft] = useState({});

    const hasData = !!comparison?.has_supplier_data;
    const supplier = comparison?.supplier;
    const supplierName = suppliers.find((s) => s.id === supplierId)?.name || "";

    useEffect(() => {
        api.get("/suppliers").then((r) => setSuppliers(r.data)).catch(() => {});
        api.get("/steel-grades").then((r) => setGrades(r.data)).catch(() => {});
    }, []);

    // Once both supplier + grade are picked, auto-populate rm_rate from the
    // supplier's own steel rate (requirement #2: "rate auto-populates the
    // same way ours does"). Falls back to a blank, still-editable field when
    // no rate is on file yet for that supplier × grade combination.
    useEffect(() => {
        if (!supplierId || !gradeId) return;
        api.get("/supplier-steel-rates", { params: { supplier_id: supplierId, grade_id: gradeId } })
            .then((r) => {
                const rate = r.data?.[0];
                if (rate) setManualDraft((d) => ({ ...d, rm_rate: rate.rate_per_kg }));
            })
            .catch(() => {});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [supplierId, gradeId]);

    const onFilePick = (e) => {
        const f = e.target.files?.[0];
        if (f) onUpload(f);
    };

    const submitManual = () => {
        const values = {};
        for (const [k, v] of Object.entries(manualDraft)) {
            const n = parseFloat(v);
            if (!isNaN(n)) values[k] = n;
        }
        if (Object.keys(values).length === 0) {
            toast.error("Enter at least one cost value");
            return;
        }
        onManual(supplierName.trim(), values);
        setMode(null);
        setManualDraft({});
        setSupplierId("");
        setGradeId("");
    };

    return (
        <Card data-testid="supplier-zbc-card">
            <div className="p-4 border-b border-slate-100 flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <FileSpreadsheet className="w-4 h-4 text-[hsl(var(--copper))]" />
                    <div className="text-[13px] font-semibold text-slate-800">
                        Supplier ZBC Comparison
                    </div>
                    {hasData && (
                        <Pill tone="emerald" className="text-[10px]">
                            {supplier?.source === "MANUAL" ? "Manual entry" : "From workbook"}
                        </Pill>
                    )}
                </div>
                <div className="flex items-center gap-2">
                    {!hasData && (
                        <>
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept=".xlsx,.xlsm"
                                hidden
                                onChange={onFilePick}
                                data-testid="supplier-zbc-file-input"
                            />
                            <SlateButton
                                variant="outline"
                                onClick={() => fileInputRef.current?.click()}
                                disabled={busy}
                                className="h-7 text-[11px]"
                                data-testid="supplier-zbc-upload-btn"
                            >
                                {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <UploadCloud className="w-3 h-3" />}
                                Upload supplier .xlsx
                            </SlateButton>
                            <SlateButton
                                variant="outline"
                                onClick={() => setMode(mode === "manual" ? null : "manual")}
                                className="h-7 text-[11px]"
                                data-testid="supplier-zbc-manual-btn"
                            >
                                <Pencil className="w-3 h-3" /> Enter manually
                            </SlateButton>
                        </>
                    )}
                    {hasData && (
                        <SlateButton
                            variant="ghost"
                            onClick={onClear}
                            className="h-7 px-2 text-[11px] text-red-600 hover:bg-red-50"
                            data-testid="supplier-zbc-clear-btn"
                        >
                            <Trash2 className="w-3 h-3" /> Clear
                        </SlateButton>
                    )}
                </div>
            </div>

            {/* Manual entry mini-form */}
            {!hasData && mode === "manual" && (
                <div className="p-4 border-b border-slate-100 bg-amber-50/40 space-y-3" data-testid="supplier-zbc-manual-form">
                    <div className="text-[11px] text-slate-600">
                        Enter the supplier&#39;s cost breakdown (all fields optional, leave blank if unknown).
                        <span className="ml-1 text-slate-500">The comparison table below auto-refreshes.</span>
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                        <Field label="Supplier" helper="From the Suppliers master">
                            <Select
                                value={supplierId}
                                onChange={(e) => setSupplierId(e.target.value)}
                                data-testid="supplier-zbc-manual-name"
                            >
                                <option value="">Select supplier…</option>
                                {suppliers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                            </Select>
                        </Field>
                        <Field label="Steel Grade (assumed)" helper="Auto-fills their RM rate below, if on file">
                            <Select
                                value={gradeId}
                                onChange={(e) => setGradeId(e.target.value)}
                                data-testid="supplier-zbc-manual-grade"
                            >
                                <option value="">Select grade…</option>
                                {grades.map((g) => <option key={g.id} value={g.id}>{g.code}</option>)}
                            </Select>
                        </Field>
                        {WEIGHT_RATE_FIELDS.map((f) => (
                            <Field key={f.key} label={f.label}>
                                <NumberInput
                                    value={manualDraft[f.key] ?? ""}
                                    onChange={(e) => setManualDraft({ ...manualDraft, [f.key]: e.target.value })}
                                    step="0.001"
                                    data-testid={`supplier-zbc-manual-${f.key}`}
                                />
                            </Field>
                        ))}
                        <Field label="RM Rate (₹/kg)" helper={gradeId ? "Auto-filled from Supplier Steel Rates, editable" : "Pick a steel grade to auto-fill"}>
                            <NumberInput
                                value={manualDraft.rm_rate ?? ""}
                                onChange={(e) => setManualDraft({ ...manualDraft, rm_rate: e.target.value })}
                                step="0.01"
                                data-testid="supplier-zbc-manual-rm_rate"
                            />
                        </Field>
                        {MANUAL_FIELDS.map((f) => (
                            <Field key={f.key} label={f.label}>
                                <NumberInput
                                    value={manualDraft[f.key] ?? ""}
                                    onChange={(e) => setManualDraft({ ...manualDraft, [f.key]: e.target.value })}
                                    step="0.01"
                                    data-testid={`supplier-zbc-manual-${f.key}`}
                                />
                            </Field>
                        ))}
                    </div>
                    <div className="flex justify-end gap-2">
                        <SlateButton variant="outline" onClick={() => { setMode(null); setManualDraft({}); }} className="h-7 text-[11px]">Cancel</SlateButton>
                        <CopperButton onClick={submitManual} disabled={busy} className="h-7 text-[11px]" data-testid="supplier-zbc-manual-save">
                            {busy ? "Saving…" : "Save & compare"}
                        </CopperButton>
                    </div>
                </div>
            )}

            {/* Empty state */}
            {!hasData && mode !== "manual" && (
                <div className="p-6 text-center text-[12px] text-slate-500">
                    Upload the supplier&#39;s ABC COMPANY-format ZBC workbook (.xlsx), or enter the numbers manually, to see
                    a line-by-line comparison against CostIQ&#39;s should-cost.
                </div>
            )}

            {/* Comparison table */}
            {hasData && comparison && (
                <SupplierComparisonView comparison={comparison} />
            )}
        </Card>
    );
}

function SupplierComparisonView({ comparison }) {
    const { rows, financial_impact, supplier } = comparison;
    const fi = financial_impact || {};
    const sourceLabel =
        supplier?.source === "UPLOAD"
            ? `${supplier.filename || "workbook"} · sheet "${supplier.sheet_name || "?"}"`
            : `Manual entry${supplier?.supplier_name ? ` · ${supplier.supplier_name}` : ""}`;

    const impactPositive = (fi.delta ?? 0) > 0; // supplier is higher → we overpay
    return (
        <div>
            {/* Roll-up bar */}
            <div className="px-4 py-3 border-b border-slate-100 bg-slate-50/60 flex items-center justify-between text-[12px]">
                <div className="text-slate-600">
                    <span className="font-semibold text-slate-800">Source:</span> {sourceLabel}
                </div>
                {fi.delta != null && (
                    <div className="flex items-center gap-4">
                        <div>
                            <span className="text-slate-500">Ours: </span>
                            <span className="font-mono font-semibold">{fmtINR(fi.ours_should_cost || 0, 2)}</span>
                        </div>
                        <div>
                            <span className="text-slate-500">Supplier: </span>
                            <span className="font-mono font-semibold">{fmtINR(fi.supplier_net_price || 0, 2)}</span>
                        </div>
                        <div className={`inline-flex items-center gap-1 font-mono font-semibold ${
                            impactPositive ? "text-red-600" : "text-emerald-600"
                        }`}>
                            {impactPositive ? <TrendingUp className="w-3.5 h-3.5" /> : <TrendingDown className="w-3.5 h-3.5" />}
                            {fi.delta >= 0 ? "+" : ""}{fmtINR(fi.delta, 2)}
                            <span className="text-[10.5px] opacity-75">({fi.delta_pct >= 0 ? "+" : ""}{fi.delta_pct}%)</span>
                        </div>
                        {fi.annual_saving_potential != null && (
                            <div className={`text-[11px] ${impactPositive ? "text-red-600" : "text-emerald-600"}`}>
                                Annual: {fi.annual_saving_potential >= 0 ? "+" : ""}{fmtINR(fi.annual_saving_potential)}
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Line-item comparison table */}
            <div className="overflow-x-auto">
                <table className="w-full text-[12.5px]" data-testid="supplier-comparison-table">
                    <thead className="bg-slate-50/60 border-b border-slate-200">
                        <tr>
                            <th className="text-left py-2 px-3 eyebrow text-slate-500">Metric</th>
                            <th className="text-right py-2 px-3 eyebrow text-slate-500">CostIQ (Ours)</th>
                            <th className="text-right py-2 px-3 eyebrow text-slate-500">Supplier</th>
                            <th className="text-right py-2 px-3 eyebrow text-slate-500">Delta</th>
                            <th className="text-right py-2 px-3 eyebrow text-slate-500">Δ %</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((r) => {
                            const isNetPrice = r.key === "net_price";
                            const sevColor =
                                !r.severity ? "text-slate-400" :
                                r.severity === "INFO" ? "text-slate-700" :
                                r.severity === "WARN" ? "text-amber-700" :
                                "text-red-700";
                            return (
                                <tr
                                    key={r.key}
                                    className={`border-b border-slate-100 ${isNetPrice ? "bg-amber-50/50 font-semibold" : "hover:bg-slate-50/40"}`}
                                >
                                    <td className="py-1.5 px-3 text-slate-800">{r.label}</td>
                                    <td className="py-1.5 px-3 text-right font-mono tabular-nums">
                                        {r.ours != null ? fmtINR(r.ours, 2) : "—"}
                                    </td>
                                    <td className="py-1.5 px-3 text-right font-mono tabular-nums">
                                        {r.supplier != null ? fmtINR(r.supplier, 2) : "—"}
                                    </td>
                                    <td className={`py-1.5 px-3 text-right font-mono tabular-nums ${sevColor}`}>
                                        {r.delta != null ? (r.delta >= 0 ? "+" : "") + fmtINR(r.delta, 2) : "—"}
                                    </td>
                                    <td className={`py-1.5 px-3 text-right font-mono tabular-nums ${sevColor}`}>
                                        {r.delta_pct != null ? `${r.delta_pct >= 0 ? "+" : ""}${r.delta_pct}%` : "—"}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            <div className="px-4 py-2 text-[10.5px] text-slate-500 bg-slate-50/40">
                Positive delta = supplier is <span className="text-red-600 font-semibold">above</span> CostIQ&#39;s should-cost (potential negotiation room).
                Negative delta = supplier is <span className="text-emerald-600 font-semibold">below</span> the should-cost floor (verify quality/scope).
                Rows highlighted in <span className="text-amber-700">amber</span> exceed 5% delta, <span className="text-red-700">red</span> exceeds 15%.
            </div>
        </div>
    );
}
